import { simpleLerp } from '../../../lib/math';
import type { TimeData } from '../../core/TimeData';
import {
  isPortCallArrivalEvent,
  isPortCallDepartureEvent,
} from '../../core/TimeData';
import { memoize } from 'lodash';
import type { ScenarioTimeline_ShipFragment } from '../../../graphql';
import { memoizeN } from '@seaber/common/lib/memoize';

type DraftDatapoint = [cargoMass: number, draft: number];
enum DatapointDimension {
  MASS = 0,
  DRAFT = 1,
}

/**
 * When cargo load on the vessel is more than
 * maxDraftAtLocation * OVERDRAFT_THRESHOLD we display
 * a yellow warning;
 * When it is more than maxDraftAtLocation we display
 * a red warning
 */
const OVERDRAFT_THRESHOLD = 0.95;

/**
 *  The severity of vessel overdraft
 *  'warning' if (max-draft-at-port * OVERDRAFT_THRESHOLD < vessel-draft < max-draft-at-port)
 *  'error' if (max-draft-at-port < vessel-draft)
 */
export type OverDraftWarningSeverity = 'warning' | 'error';

/**
 *  Extracts vessel draft data points for estimation of an actual draft
 *  of the vessel at a given cargo load.
 *  @param nodes
 *  @returns an array of draft datapoints, where each datapoint
 *  is a tuple of `[cargoLoad, draftAtLoad]:[number, number]`
 */
export const getVesselDraftDatapoints = memoize(
  (vessel: ScenarioTimeline_ShipFragment): Array<DraftDatapoint> => {
    if (!vessel.vesselDraftDatapointsByShipId.nodes.length) return [];

    return vessel.vesselDraftDatapointsByShipId.nodes.reduce((acc, data) => {
      if (!data) return acc;

      const { cargoLoad, draft } = data;

      if (!cargoLoad || !draft) return acc;

      acc.push([Number(cargoLoad), Number(draft)]);
      return acc;
    }, [] as Array<DraftDatapoint>);
  },
);

/**
 * Generic function to find line segment containing a target value.
 * @param datapoints - Array of [x, y] tuples sorted by x
 * @param targetValue - Value to search for
 * @param dimension - 0 for x-axis (cargo load), 1 for y-axis (draft)
 */
const getDatapointSegment = function <T extends DraftDatapoint>(
  datapoints: Array<T>,
  targetValue: number,
  dimension: DatapointDimension,
): [T, T] | undefined {
  const sortedDatapoints = datapoints.sort((a, b) => a[0] - b[0]);
  if (sortedDatapoints.length <= 1) return undefined;

  if (targetValue <= sortedDatapoints[0][dimension]) {
    return sortedDatapoints.slice(0, 2) as [T, T];
  }
  if (targetValue >= sortedDatapoints[sortedDatapoints.length - 1][dimension]) {
    return sortedDatapoints.slice(
      sortedDatapoints.length - 2,
      sortedDatapoints.length,
    ) as [T, T];
  }

  for (let i = 0; i < sortedDatapoints.length - 1; i++) {
    const currentPoint = sortedDatapoints[i];
    const nextPoint = sortedDatapoints[i + 1];

    if (
      targetValue >= currentPoint[dimension] &&
      targetValue < nextPoint[dimension]
    ) {
      return [currentPoint, nextPoint];
    }
  }
  return undefined;
};

/**
 * Calculates the line segment containing specified cargoLoad from a set
 * of vessel datapoints.
 *
 * E.g. given a set of `[[1, 3], [3, 6], [7, 10]]` and `cargoLoad` of `4`
 * it will return line segment `[[3, 6], [7, 10]]`
 */
export const getDraftDatapointSegmentFromLoad = function <
  T extends DraftDatapoint,
>({
  draftDatapoints,
  cargoLoad,
}: {
  draftDatapoints: Array<T>;
  cargoLoad: number;
}): [T, T] | undefined {
  return getDatapointSegment(
    draftDatapoints,
    cargoLoad,
    DatapointDimension.MASS,
  );
};

/**
 * Calculates the line segment containing specified draft from a set
 * of vessel datapoints. Inverse of getDraftDatapointSegmentFromLoad.
 *
 * E.g. given a set of `[[1000, 5], [2000, 8], [3000, 12]]` and `draft` of `9`
 * it will return line segment `[[2000, 8], [3000, 12]]`
 */
export const getDraftDatapointSegmentFromDraft = function <
  T extends DraftDatapoint,
>({
  draftDatapoints,
  draft,
}: {
  draftDatapoints: Array<T>;
  draft: number;
}): [T, T] | undefined {
  return getDatapointSegment(draftDatapoints, draft, DatapointDimension.DRAFT);
};

/**
 * Calculates maximum cargo load a vessel can carry given a draft constraint.
 * Inverse of estimateActualVesselDraft
 *
 * @param draftDatapoints - Array of [cargoLoad, draft] tuples
 * @param targetDraft - Maximum allowed draft at port (meters)
 * @returns Maximum cargo load (tons), or undefined if cannot determine. 0 if no capacity satisfies the draft.
 */
export const estimateCargoLoadFromDraft = (
  draftDatapoints: Array<DraftDatapoint>,
  targetDraft: number,
): number | undefined => {
  if (draftDatapoints.length === 0) return undefined;

  const sortedPoints = [...draftDatapoints].sort((a, b) => a[0] - b[0]);

  if (sortedPoints[0][DatapointDimension.DRAFT] > targetDraft) {
    return 0;
  }

  if (
    sortedPoints[sortedPoints.length - 1][DatapointDimension.DRAFT] <=
    targetDraft
  ) {
    return sortedPoints[sortedPoints.length - 1][DatapointDimension.MASS];
  }

  const segment = getDraftDatapointSegmentFromDraft({
    draftDatapoints: sortedPoints,
    draft: targetDraft,
  });

  if (!segment) return undefined;

  try {
    const [load1, draft1] = segment[0];
    const [load2, draft2] = segment[1];
    const segmentAsDraftToLoad: [DraftDatapoint, DraftDatapoint] = [
      [draft1, load1],
      [draft2, load2],
    ];
    return simpleLerp(targetDraft, segmentAsDraftToLoad);
  } catch (_) {
    return undefined;
  }
};

/**
 * Calculates the maximum cargo load a vessel can carry given a draft constraint.
 * If the vessel has no draft datapoints, returns 0 if the vessel's draft is more than the target draft, otherwise undefined.
 */
export const getVesselCargoLoadFromDraft = memoizeN(
  (vessel: ScenarioTimeline_ShipFragment, draft: number) => {
    const draftDatapoints = getVesselDraftDatapoints(vessel);
    if (draftDatapoints.length === 0) {
      return vessel.draft && +vessel.draft > draft ? 0 : undefined;
    }
    return estimateCargoLoadFromDraft(draftDatapoints, draft);
  },
);

type DraftEstimationData = { usedMassCapacity: number; maxPortDraft: number };
const UNDEFINED_DRAFT_ESTIMATION_DATA: Partial<DraftEstimationData> = {
  usedMassCapacity: undefined,
  maxPortDraft: undefined,
};
/**
 *  Checks incoming timedata to make sure it represents either
 *  departure or arrival port call event,
 *  extracts data used to estimate actual draft of the vessel at the event
 */
const getDraftEstimationDataFromTimeData = memoize(function (
  td: TimeData | undefined,
): Partial<DraftEstimationData> {
  if (
    !td ||
    !td.event ||
    (!isPortCallArrivalEvent(td.event) &&
      !isPortCallDepartureEvent(td.event)) ||
    !td.event.location ||
    !td.event.location.draft
  )
    return UNDEFINED_DRAFT_ESTIMATION_DATA;
  const { usedMassCapacity } = td;

  // Skip if there is no cargo load
  if (usedMassCapacity === 0) return UNDEFINED_DRAFT_ESTIMATION_DATA;
  const maxPortDraft = Number(td.event.location.draft);
  return { usedMassCapacity, maxPortDraft };
});

export type EventDraftIssue = {
  draftIssueSeverity: OverDraftWarningSeverity;
  estimatedVesselDraft: number;
  portMaxDraft: number;
};

/**
 * Calculates the severity of vessel overdraft at a port based on params.
 *
 * @param coefficients for linear regression interpolation
 * @param portMaxDraft max allowed draft at a given port
 * @param usedMassCapacity used mass capacity of the vessel
 * @returns `EventDraftIssues` object, or undefined if no overdraft was detected
 * The resulting object contains data on the estimated vessel draft and max allowed
 * draft at location for ease of display. The severity of the warning works as follows:
 *  - 'warning' if vessel draft almost exceeds max draft at port, according to `OVERDRAFT_THRESHOLD`;
 *  - 'error' if vessel draft exceeds max draft at port
 *  - `undefined` if no overdraft was detected
 */
const getPortDraftWarningInfo = function (
  estimatedVesselDraft: number,
  portMaxDraft: number,
): EventDraftIssue | undefined {
  let draftIssueSeverity: OverDraftWarningSeverity | undefined = undefined;
  if (estimatedVesselDraft - Number(portMaxDraft) * OVERDRAFT_THRESHOLD > 0) {
    draftIssueSeverity = 'warning';
  }
  if (estimatedVesselDraft > Number(portMaxDraft)) {
    draftIssueSeverity = 'error';
  }
  if (!draftIssueSeverity) return undefined;
  return {
    draftIssueSeverity,
    portMaxDraft: Number(portMaxDraft),
    estimatedVesselDraft,
  };
};

const isSafeDraftEstimationData = (
  data: Partial<DraftEstimationData>,
): data is DraftEstimationData =>
  data.maxPortDraft !== undefined && data.usedMassCapacity !== undefined;

/**
 * Gets a vessel draft warning for a given event, if any.
 */
export const getDraftWarningAtEvent = memoize(
  (event: TimeData): EventDraftIssue | undefined => {
    const draftEstimationData = event
      ? getDraftEstimationDataFromTimeData(event)
      : UNDEFINED_DRAFT_ESTIMATION_DATA;
    const estimatedDraft =
      event?.event?.vessel && isSafeDraftEstimationData(draftEstimationData)
        ? estimateActualVesselDraft(event.event.vessel)(draftEstimationData)
        : undefined;
    if (draftEstimationData.maxPortDraft && estimatedDraft)
      return getPortDraftWarningInfo(
        estimatedDraft,
        draftEstimationData.maxPortDraft,
      );
    return undefined;
  },
);

/**
 * Calculates estimated draft of a vessel, given its used mass capacity.
 * Uses vessel draft datapoints to perform the estimation, if no datapoints
 * are available returns `vessel.draft` as a fallback value.
 *
 * @param vessel
 * @returns estimated vessel draft or null
 */
export const estimateActualVesselDraft = memoize(
  (vessel: ScenarioTimeline_ShipFragment) => {
    const draftValueFallback = vessel.draft ? Number(vessel.draft) : undefined;
    const draftDatapoints = getVesselDraftDatapoints(vessel);
    return memoize(({ usedMassCapacity }: { usedMassCapacity: number }) => {
      const datapointSegment = getDraftDatapointSegmentFromLoad({
        draftDatapoints,
        cargoLoad: usedMassCapacity,
      });
      if (!datapointSegment) return draftValueFallback;
      try {
        return simpleLerp(usedMassCapacity, datapointSegment);
      } catch (_) {
        return draftValueFallback;
      }
    });
  },
);

export const getVesselDraftFromLoadedMass = memoizeN(
  (
    vessel: ScenarioTimeline_ShipFragment,
    usedMassCapacity: number,
  ): number | undefined =>
    estimateActualVesselDraft(vessel)({ usedMassCapacity }),
);
